/**
 * AuthGuard — защищает роуты.
 *
 * Логика:
 * 1. Если dev-bypass активен — пропускаем, при необходимости логиним автоматически.
 * 2. Иначе — требуется accessToken в store (или localStorage).
 * 3. Если ничего нет — редирект на /login.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import { isDevBypassEnabled } from '@/lib/devBypass';
import { useAuthStore } from '@/store/authStore';

interface Props {
  children: ReactNode;
}

export function AuthGuard({ children }: Props) {
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [bypassTried, setBypassTried] = useState(false);
  const qc = useQueryClient();
  void qc; // пока не используем, оставлен для будущей интеграции

  // Dev-bypass: пытаемся авто-логиниться
  useEffect(() => {
    if (!isDevBypassEnabled()) return;
    if (isAuthenticated) {
      setBypassTried(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const { devBypassLogin } = await import('@/lib/devBypass');
      if (cancelled) return;
      await devBypassLogin();
      setBypassTried(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Dev-bypass активен
  if (isDevBypassEnabled()) {
    // Ждём завершения bypass-попытки
    if (!isAuthenticated && !bypassTried) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-surface-950 text-surface-400">
          <div className="text-sm">Dev-bypass login…</div>
        </div>
      );
    }
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <>{children}</>;
  }

  // Обычный режим — нужен токен
  if (!accessToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
